import { useEffect, useState } from 'react'
import { Button, Drawer, Empty, List, Popconfirm, Space, Typography, message } from 'antd'
import { DeleteOutlined, PlayCircleOutlined } from '@ant-design/icons'
import { translate } from '../../../../core'
import { aiAssistantService } from '../service'
import { SavedReport, SavedReportRun } from '../type/ai-assistant'
import { requestErrorMessage } from '../ai-assistant.utils'

interface Props {
  open: boolean
  onClose: () => void
  /** Show the re-run result in the conversation. */
  onRan: (report: SavedReport, run: SavedReportRun) => void
}

/**
 * Reports an admin saved from an answer they checked. Re-running one makes no
 * model call: the server re-runs the SQL it stored, so it is free and repeatable.
 */
export const SavedReportsDrawer = ({ open, onClose, onRan }: Props) => {
  const [reports, setReports] = useState<SavedReport[]>([])
  const [loading, setLoading] = useState(false)
  const [running, setRunning] = useState<number | null>(null)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    aiAssistantService
      .savedReports()
      .then(setReports)
      .catch((error) => message.error(requestErrorMessage(error)))
      .finally(() => setLoading(false))
  }, [open])

  const run = async (report: SavedReport) => {
    setRunning(report.id)
    try {
      onRan(report, await aiAssistantService.runSavedReport(report.id))
      onClose()
    } catch (error) {
      message.error(requestErrorMessage(error))
    } finally {
      setRunning(null)
    }
  }

  const remove = async (report: SavedReport) => {
    try {
      await aiAssistantService.deleteSavedReport(report.id)
      setReports((current) => current.filter((r) => r.id !== report.id))
    } catch (error) {
      message.error(requestErrorMessage(error))
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title={translate('ai_reports_saved_reports')} width={420}>
      <Typography.Paragraph type="secondary">{translate('ai_reports_saved_reports_hint')}</Typography.Paragraph>
      <List
        loading={loading}
        dataSource={reports}
        locale={{ emptyText: <Empty description={translate('ai_reports_no_saved_reports')} /> }}
        renderItem={(report) => (
          <List.Item
            actions={[
              <Button
                key="run"
                type="link"
                icon={<PlayCircleOutlined />}
                loading={running === report.id}
                onClick={() => run(report)}
              >
                {translate('run')}
              </Button>,
              <Popconfirm
                key="delete"
                title={translate('ai_reports_delete_saved_report')}
                okText={translate('delete')}
                cancelText={translate('cancel')}
                onConfirm={() => remove(report)}
              >
                <Button type="link" danger icon={<DeleteOutlined />} />
              </Popconfirm>,
            ]}
          >
            <List.Item.Meta
              title={report.name}
              description={
                <Space direction="vertical" size={0}>
                  <Typography.Text type="secondary">{report.question}</Typography.Text>
                  {report.lastRunAt && (
                    <Typography.Text type="secondary">
                      {translate('ai_reports_last_run')} {new Date(report.lastRunAt).toLocaleString()}
                    </Typography.Text>
                  )}
                </Space>
              }
            />
          </List.Item>
        )}
      />
    </Drawer>
  )
}
